// Utilidades sin IA para convertir entre texto (Markdown) y mapas mentales (React Flow).

const EDGE_STYLE = { stroke: "#6366f1", strokeWidth: 2 };
const MARKER = { type: "arrowclosed", color: "#6366f1" };

// Convierte un texto tipo esquema (títulos # y viñetas -) en una lista {depth, text}.
export function parseOutline(content) {
  const lines = (content || "").split(/\r?\n/);
  const items = [];
  let lastHeadingDepth = -1;
  for (const line of lines) {
    if (!line.trim()) continue;
    const h = line.match(/^(#{1,6})\s+(.+)/);
    if (h) {
      const depth = h[1].length - 1;
      lastHeadingDepth = depth;
      items.push({ depth, text: h[2].trim() });
      continue;
    }
    const b = line.match(/^(\s*)(?:[-*+]|\d+[.)])\s+(.+)/);
    if (b) {
      const indent = b[1].replace(/\t/g, "  ").length;
      const base = lastHeadingDepth >= 0 ? lastHeadingDepth + 1 : 0;
      items.push({ depth: base + Math.floor(indent / 2), text: b[2].trim() });
      continue;
    }
    const base = lastHeadingDepth >= 0 ? lastHeadingDepth + 1 : 0;
    items.push({ depth: base, text: line.trim() });
  }
  return items;
}

function layout(nodes, edges) {
  const children = {};
  edges.forEach((e) => { (children[e.source] ||= []).push(e.target); });
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  let cursor = 0;
  const X = 320;
  const Y = 96;
  function place(id, depth) {
    const n = byId[id];
    n.position.x = depth * X;
    const kids = children[id] || [];
    if (!kids.length) {
      n.position.y = cursor;
      cursor += Y;
    } else {
      const ys = kids.map((k) => place(k, depth + 1));
      n.position.y = (Math.min(...ys) + Math.max(...ys)) / 2;
    }
    return n.position.y;
  }
  place(nodes[0].id, 0);
}

// Detecta el color de rama según palabras clave de presupuesto.
function branchColor(label) {
  const t = (label || "").toLowerCase();
  if (/ingres|entrada|aporte/.test(t)) return "green";
  if (/egres|gasto|salida|pago/.test(t)) return "red";
  if (/haber|balance|saldo|disponible|neto/.test(t)) return "neutral";
  return null;
}

// Paleta viva para el "Modo Niños": cada rama principal un color distinto.
const KIDS_PALETTE = ["sky", "pink", "amber", "violet", "lime", "orange", "teal", "fuchsia"];

// Extrae un emoji inicial de un texto ("🐶 Perro" -> {emoji:"🐶", text:"Perro"}).
let EMOJI_RE = null;
try {
  EMOJI_RE = new RegExp("^((?:\\p{Extended_Pictographic})(?:\\u200d\\p{Extended_Pictographic})*[\\uFE0F\\u20E3]?)\\s+", "u");
} catch {
  EMOJI_RE = null;
}
function splitEmoji(text) {
  if (!EMOJI_RE) return { emoji: "", text: text || "" };
  const m = EMOJI_RE.exec(text || "");
  if (m) return { emoji: m[1], text: text.slice(m[0].length).trim() };
  return { emoji: "", text: text || "" };
}

// Esquema/nota -> mapa mental {nodes, edges}. kids=true colorea con paleta viva.
export function outlineToMindmap(title, content, kids = false) {
  const now = new Date().toISOString();
  const nodes = [{
    id: "root",
    type: "mindnode",
    _parent: null,
    position: { x: 0, y: 0 },
    data: { label: title || "Idea central", createdAt: now, updatedAt: now, history: [], isRoot: true },
  }];
  const edges = [];
  const stack = [{ id: "root", depth: -1 }];
  const colorById = { root: null };
  let seq = 1;
  let branchIdx = 0;
  const base = Date.now();
  for (const it of parseOutline(content)) {
    while (stack.length > 1 && stack[stack.length - 1].depth >= it.depth) stack.pop();
    const parent = stack[stack.length - 1];
    const id = `n${base}_${seq++}`;
    const { emoji, text } = splitEmoji(it.text);
    let color = colorById[parent.id];
    if (!color) {
      if (kids) {
        color = parent.id === "root" ? KIDS_PALETTE[branchIdx++ % KIDS_PALETTE.length] : null;
      } else {
        color = branchColor(text);
      }
    }
    colorById[id] = color;
    nodes.push({
      id, type: "mindnode", _parent: parent.id, position: { x: 0, y: 0 },
      data: { label: text, emoji: emoji || undefined, createdAt: now, updatedAt: now, history: [], isRoot: false, color: color || undefined },
    });
    edges.push({ id: `e${parent.id}-${id}`, source: parent.id, target: id, type: "smoothstep", style: EDGE_STYLE, markerEnd: MARKER });
    stack.push({ id, depth: it.depth });
  }
  layout(nodes, edges);
  return { nodes, edges };
}

// Mapa mental -> Markdown (viñetas anidadas)
export function mindmapToMarkdown(title, nodes, edges) {
  const children = {};
  edges.forEach((e) => { (children[e.source] ||= []).push(e.target); });
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const root = nodes.find((n) => n.data?.isRoot) || byId["root"] || nodes[0];
  const lines = [`# ${title || root?.data?.label || "Mapa"}`, ""];
  function walk(id, depth) {
    for (const cid of children[id] || []) {
      const label = byId[cid]?.data?.label || "";
      lines.push(`${"  ".repeat(Math.max(depth, 0))}- ${label}`);
      walk(cid, depth + 1);
    }
  }
  if (root) walk(root.id, 0);
  return lines.join("\n");
}
